import React from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TableSortLabel,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import spacing from '../spacing';
import shadows from '../shadows';
import typography from '../typography';

/**
 * Custom Table Component
 * Tabla moderna con sorting, paginación y diseño responsive
 */

const StyledTableContainer = styled(TableContainer)({
  borderRadius: spacing.borderRadius.lg,
  boxShadow: shadows.card,
  overflow: 'hidden',
});

const StyledTable = styled(MuiTable)(({ theme }) => ({
  minWidth: 650,
  
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  
  '& .MuiTableCell-head': {
    color: theme.palette.primary.contrastText,
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.sm,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: `${spacing[3]}px ${spacing[4]}px`,
    borderBottom: 'none',
  },
  
  '& .MuiTableSortLabel-root': {
    color: theme.palette.primary.contrastText,
    '&:hover': {
      color: theme.palette.primary.contrastText,
    },
    '&.Mui-active': {
      color: theme.palette.primary.contrastText,
    },
  },
  
  '& .MuiTableSortLabel-icon': {
    color: 'inherit',
  },
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  
  '& .MuiTableCell-body': {
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.fontSize.sm,
  },
}));

const StyledTableCell = styled(TableCell)(({ align, highlight, theme }) => ({
  ...(highlight && {
    backgroundColor: theme.palette.action.selected,
    fontWeight: typography.fontWeight.semibold,
    color: theme.palette.primary.main,
  }),
  
  ...(align === 'center' && {
    textAlign: 'center',
  }),
  
  ...(align === 'right' && {
    textAlign: 'right',
  }),
}));

// Componente con estado interno para sorting y paginación
const Table = ({
  columns,
  data,
  pagination = false,
  sortable = false,
  striped = true,
  hover = true,
  dense = false,
  onRowClick,
  ...props
}) => {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleRequestSort = (property) => {
    if (!sortable) return;
    
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = React.useMemo(() => {
    if (!sortable || !orderBy) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (aValue === bValue) return 0;
      
      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [data, order, orderBy, sortable]);

  const paginatedData = React.useMemo(() => {
    if (!pagination) return sortedData;
    
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage, pagination]);

  return (
    <Box>
      <StyledTableContainer component={Paper}>
        <StyledTable size={dense ? 'small' : 'medium'} {...props}>
          <StyledTableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                >
                  {sortable && column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          
          <StyledTableBody>
            {paginatedData.map((row, index) => (
              <TableRow
                key={index}
                hover={hover}
                onClick={() => onRowClick && onRowClick(row)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align || 'left'}
                    highlight={column.highlight}
                  >
                    {column.format
                      ? column.format(row[column.id], row)
                      : row[column.id]}
                  </StyledTableCell>
                ))}
              </TableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledTableContainer>
      
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}
    </Box>
  );
};

export default Table;
